import React, { useEffect, useState, useContext } from "react";
import { Typography } from "antd";
import { Parser } from "json2csv";

import {
  DateFormat,
  errorMessage,
  getApi,
  saveApi,
  successMessage,
} from "../../../helpers";
import Badge from "../../../components/Badge";
import CardContainer from "../../../components/Card";
import TableContainer from "../../../components/Table";
import VariantPopup from "./Popup";
import VariantSidebar from "./Sidebar";
import Popup from "../../../components/Popup";
import { StoreContext } from "../../../context";
import StockPopup from "../StockPopup";
import { canWrite } from "../../../helpers/permissions";

const saveAs = require("file-saver");

const allColumns = [
  {
    title: "SKU",
    dataIndex: "variant_sku",
    key: "variant_sku",
  },
  {
    title: "Variant",
    dataIndex: "variant_name",
    key: "variant_name",
  },
  {
    title: "Product Name",
    dataIndex: "product_name",
    key: "product_name",
  },
  {
    title: "Quantity",
    dataIndex: "variant_stock",
    key: "variant_stock",
    align: "center",
    sorter: true,
    render: (text) => <div style={{ textAlign: "right" }}>{text}</div>,
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    align: "center",
    sorter: true,
    render: (text, object) => (
      <div style={{ textAlign: "right" }}>
        ₹{text === "0.00" ? object.mrp : text}
      </div>
    ),
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    sorter: true,
    render: (text) => DateFormat(text),
  },
  {
    title: "Status",
    key: "variant_status",
    dataIndex: "variant_status",
    align: "center",
    sorter: true,
    render: (text, object, index) => <Badge productStatus={text} />,
  },
  {
    title: "Active",
    dataIndex: "store_is_active",
    key: "store_is_active",
    align: "center",
    sorter: true,
    render: (text, object, index) => <Badge active={text} />,
  },
];

const mainColumns = [
  {
    title: "SKU",
    dataIndex: "variant_sku",
    key: "variant_sku",
  },
  {
    title: "Variant",
    dataIndex: "variant_name",
    key: "variant_name",
  },
  {
    title: "Product Name",
    dataIndex: "product_name",
    key: "product_name",
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    align: "center",
    render: (text, object) => (
      <div style={{ textAlign: "right" }}>
        ₹{text === "0.00" ? object.mrp : text}
      </div>
    ),
  },
  {
    title: "Cost Price",
    dataIndex: "cost_price",
    key: "cost_price",
    align: "center",
    render: (text) => <div style={{ textAlign: "right" }}>₹{text}</div>,
  },
  {
    title: "MRP",
    dataIndex: "mrp",
    key: "mrp",
    align: "center",
    render: (text) => <div style={{ textAlign: "right" }}>₹{text}</div>,
  },
  {
    title: "Status",
    key: "variant_status",
    dataIndex: "variant_status",
    align: "center",
    sorter: true,
    render: (text, object, index) => <Badge productStatus={text} />,
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    sorter: true,
    render: (text) => DateFormat(text),
  },
  {
    title: "Active",
    dataIndex: "variant_is_active",
    key: "variant_is_active",
    align: "center",
    sorter: true,
    render: (text, object, index) => <Badge active={text} />,
  },
];

export default function Variant({ stores }) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [savedPayload, setSavedPayload] = useState({});
  const [total, setTotal] = useState(8);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [sidebar, setSidebar] = useState(false);
  const [popup, setPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [productOptions, setProductOptions] = useState([
    { label: "Type to Search Product", disabled: true },
  ]);
  const [exportPopup, setExportPopup] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [searchedCategory, setSearchedCategory] = useState(false);

  useEffect(() => {
    getList(savedPayload);
    getCategoryOptions();
  }, [storeId]);

  const getList = async (payload = {}) => {
    if (!payload?.start_date) {
      setSelectedVariants([]);
      setLoading(true);
      if (storeId) payload = { ...payload, store_id: storeId };
      else delete payload.store_id;
      const res = await getApi(payload, "getVariants");
      if (res.data) {
        setList(res.data);
        setTotal(res.meta?.total);
      }
    }
    setLoading(false);
    setSavedPayload(payload);
  };

  const getProductOptions = async (keyword = "") => {
    setProductOptions([{ label: "Searching...", disabled: true }]);
    let payload = { size: 100, keyword: keyword };
    if (storeId) payload = { ...payload, store_id: storeId };
    const res = await getApi(payload, "getProducts");
    if (res.data) {
      const pOpts = res.data.map(
        (product) =>
          (product = { value: product.name, key: product.product_id })
      );
      if (pOpts.length) {
        setProductOptions(pOpts);
      } else {
        setProductOptions([{ label: "No Such Product", disabled: true }]);
      }
    }
  };

  const getCategoryOptions = async (keyword = '') => {
    const res = await getApi(
      { size: 100, keyword: keyword, type: "admin" },
      "getCategory"
    );
    if (res.data) {
      setCategoryOptions(res.data);
      setSearchedCategory(true);
    }
  };

  const saveHandler = async (values) => {
    setSidebar(false);
    setPopup(false);
    setLoading(true);
    let msg = "Added Variant";
    let apiName = "createVariant";
    if (storeId) {
      apiName = "updateVariant";
      msg = "Updated Stock";
      values = { ...selected, ...values, store_id: storeId };
    } else if (selected.variant_id) {
      apiName = "updateVariant";
      msg = "Updated Variant";
      values = { ...values, variant_id: selected.variant_id };
    }
    const res = await saveApi(values, msg, apiName);
    if (res) {
      getList(savedPayload);
    } else {
      setLoading(false);
    }
  };

  const exportHandler = async () => {
    let payload = storeId ? { store_id: storeId } : {};
    setExporting(true);
    if (selectedVariants.length > 0) {
      payload = { ...payload, type: "id", variant_id: selectedVariants };
    } else if (savedPayload.start_date) {
      payload = {
        ...payload,
        type: "date",
        start_date: savedPayload.start_date,
        end_date: savedPayload.end_date,
      };
    }
    const res = await getApi(payload, "exportVaraintCsv");
    let data = res?.data ? res.data : [];
    if (data && data.length > 0) {
      let headerObj = {
        ref_id: "ITEM ID",
        product_name: "PRODUCT NAME",
        variant_name: "VARIANT NAME",
        description: "PRODUCT DESCRIPTION",
        unit: "UNIT",
        weight: "WEIGHT",
        category_name: "CATEGORY",
        sub_category_name: "SUBCATEGORY",
        sub_category_group_name: "SUBCATEGORY GROUP",
      };
      if (storeId) {
        data.unshift({
          ...headerObj,
          cost_price: "COST PRICE",
          price: "DISCOUNTED PRICE",
          mrp: "MRP",
          stock: "BATCH STOCK",
          store_is_active: "ACTIVE",
          store_id: "STORE ID",
          fetched_at: "FETCHED AT",
        });
      } else {
        data.unshift({
          ...headerObj,
          fetched_at: "FETCHED AT",
        });
      }
      const json2csvParser = new Parser({ header: false });
      const csv = json2csvParser.parse(data);
      const blob = new Blob(["\ufeff", csv]);
      const pad = (n) => String(n).padStart(2, "0");
      const now = new Date();
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate()
      )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(
        now.getSeconds()
      )}`;
      const fileName = `Rapsap_variant_stock_${storeId || "all"}_${timestamp}.csv`;
      saveAs.saveAs(blob, fileName);
      setSelectedVariants([]);
      setExporting(false);
      setExportPopup(false);
      successMessage("Product Variants Exported");
    } else {
      setExporting(false);
      errorMessage("There are no product variants for selected date range");
      setExportPopup(false);
      setSelectedVariants([]);
    }
  };

  return (
    <>
      {exportPopup && (
        <Popup
          saveText="Yes, Export"
          title="Export Product Variants"
          saveHandler={exportHandler}
          visible
          width={380}
          loading={exporting}
          cancelHandler={() => setExportPopup(false)}
        >
          <Typography.Text style={{ fontSize: 15, textAlign: "left" }}>
            Are you sure to export product variants in a csv file ?
          </Typography.Text>
        </Popup>
      )}
      {sidebar && (
        <VariantSidebar
          visible={sidebar}
          data={selected}
          onClose={() => setSidebar(false)}
          onUploadDone={() => (setSidebar(false), getList(savedPayload))}
          editVariantHandler={() => {
            setPopupTitle("Edit Variant Details");
            setPopup(true);
          }}
        />
      )}
      {popup && !storeId && (
        <VariantPopup
          title={popupTitle}
          visible
          data={selected}
          getProductOptions={getProductOptions}
          productOptions={productOptions}
          storeOptions={stores}
          cancelHandler={() => setPopup(false)}
          saveHandler={saveHandler}
        />
      )}
      {popup && storeId && (
        <StockPopup
          data={selected}
          visible
          saveHandler={saveHandler}
          cancelHandler={() => setPopup(false)}
        />
      )}
      <CardContainer bordered={false} hoverable={false}>
        <TableContainer
          breadcrumbTitle="Variant Management"
          loading={loading}
          total={total}
          columns={storeId ? allColumns : mainColumns}
          data={list}
          searchFor="Variant, SKU"
          addButton={canWrite() && (storeId ? false : true)}
          functionsHandler={getList}
          rowClickHandler={(record) => (setSidebar(true), setSelected(record))}
          addHandler={() => {
            setSelected({});
            setPopupTitle("Add Variant");
            setPopup(true);
          }}
          dropdownFilterFor="Category"
          dropdownSearchHandler={(v) => getCategoryOptions(v)}
          dropdownFilterOptions={
            categoryOptions.length > 0
              ? categoryOptions.map(
                (category) =>
                (category = {
                  value: category.name,
                  key: category.category_id,
                })
              )
              : [
                {
                  label: searchedCategory
                    ? "No Such Category"
                    : "Type to Search Category",
                  disabled: true,
                },
              ]
          }
          tagFilterOptions={
            storeId
              ? [
                  { label: "Plenty", value: "plenty" },
                  { label: "In Stock", value: "in stock" },
                  { label: "Finishing Soon", value: "finishing soon" },
                  { label: "Out Of Stock", value: "out of stock" },
                ]
              : []
          }
          combineFilters
          // rowSelection={{
          //   type: "checkbox",
          //   selectedRowKeys: selectedVariants,
          //   onChange: (rowKeys) => setSelectedVariants(rowKeys),
          // }}
          rowKey={(variant) => variant.variant_id}
          dateRangePicker
          parseHandler={() => setExportPopup(true)}
          parseButton="export"
          // parseButton={storeId ? "export" : ""}
        />
      </CardContainer>
    </>
  );
}
